// src/components/deliveryActivity/useDeliveredWeekly.js
// Serie semanal de pedidos ENTREGADOS en las últimas 53 semanas ISO (lunes a
// domingo), por empresa (comercio) o por chofer.
//
// El backend no expone una serie agregada por día/semana, así que se arma con
// 53 llamadas livianas (~150-200ms c/u), con concurrencia acotada y caché de
// módulo con TTL para que abrir/cerrar el detalle no re-dispare la batería.
//
//  - companyId → GET packages/client/status-count con dateField=deliveredAt
//    (status4): anclado a la fecha REAL de entrega.
//  - driverId  → GET packages?assignTo=<id>&status=4 por rango de CREACIÓN
//    (startDateCreation/endDateCreation, MM/DD/YYYY) leyendo totalDocs:
//    status-count no filtra por chofer (exige companyId e ignora assignTo),
//    así que la semana del chofer queda anclada a la creación del pedido —
//    sesgo menor a grano semanal (se entrega a días de creado).
//
// Se llama a Axios directo (no a los wrappers de api/packages): el wrapper de
// status-count TRAGA los errores devolviendo conteos en 0, y un error de red
// pintado como "0 entregas" es dato falso — necesitamos distinguir error
// (null → celda "sin dato" / estado de error) de cero real.
import { useEffect, useState } from "react";
import Axios from "../../config/axios-config";

const WEEKS = 53;
const MAX_CONCURRENT = 6;
const CACHE_TTL_MS = 30 * 60 * 1000;

const cache = new Map(); // "c:<id>" | "d:<id>" -> { promise: Promise<Week[]|null>, at: number }

const toIsoDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const toUsDate = (d) =>
  `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`;

// Semanas lunes→domingo, de la más antigua a la vigente (parcial). SIEMPRE con
// aritmética de calendario (setDate), nunca restando ms: `lunes − i·7·86400s`
// cruza los cambios de hora (Chile: abril/septiembre) y corre las semanas
// previas un día entero, dejando un domingo fuera de toda semana.
const buildWeeks = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  // getDay(): 0=domingo … 6=sábado → días transcurridos desde el lunes.
  const sinceMonday = (today.getDay() + 6) % 7;
  const currentMonday = new Date(today);
  currentMonday.setDate(today.getDate() - sinceMonday);
  const weeks = [];
  for (let i = WEEKS - 1; i >= 0; i -= 1) {
    const start = new Date(currentMonday);
    start.setDate(currentMonday.getDate() - i * 7);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    weeks.push({ start, end });
  }
  return weeks;
};

const fetchCompanyWeek = async (companyId, week) => {
  const params = new URLSearchParams({
    companyId,
    startDate: toIsoDate(week.start),
    endDate: toIsoDate(week.end),
    dateField: "deliveredAt",
  });
  const response = await Axios.get(`packages/client/status-count?${params.toString()}`);
  const delivered = response?.data?.status4;
  return typeof delivered === "number" ? delivered : null;
};

const fetchDriverWeek = async (driverId, week) => {
  const params = new URLSearchParams({
    page: 1,
    limit: 1,
    orderField: "createdAt",
    sortType: "desc",
    assignTo: driverId,
    status: 4,
    startDateCreation: toUsDate(week.start),
    endDateCreation: toUsDate(week.end),
  });
  const response = await Axios.get(`packages?${params.toString()}`);
  const total = response?.data?.totalDocs;
  return typeof total === "number" ? total : null;
};

// Pool simple: corre los fetch de las 53 semanas con MAX_CONCURRENT en vuelo.
const fetchSeries = async (mode, id) => {
  const weeks = buildWeeks();
  const results = new Array(weeks.length).fill(null);
  let next = 0;
  const worker = async () => {
    while (next < weeks.length) {
      const i = next;
      next += 1;
      try {
        results[i] =
          mode === "c" ? await fetchCompanyWeek(id, weeks[i]) : await fetchDriverWeek(id, weeks[i]);
      } catch {
        results[i] = null;
      }
    }
  };
  await Promise.all(Array.from({ length: MAX_CONCURRENT }, worker));

  // Si TODO falló (p.ej. 401/red), la serie completa es error → null.
  if (results.every((r) => r === null)) return null;
  return weeks.map((w, i) => ({ start: w.start, end: w.end, count: results[i] }));
};

// series: Array<{start:Date, end:Date, count:number|null}> (null = semana sin
// dato por error puntual) | null (error total → reintentar) | undefined (cargando).
// Exactamente uno de companyId/driverId; sin id el caller decide qué renderizar.
export function useDeliveredWeekly({ companyId, driverId }, reloadKey = 0) {
  const mode = companyId ? "c" : driverId ? "d" : null;
  const id = companyId || driverId || null;
  const key = mode ? `${mode}:${id}` : null;
  const [series, setSeries] = useState(undefined);

  useEffect(() => {
    if (!key) return undefined;
    let alive = true;
    const cached = cache.get(key);
    if (!cached || Date.now() - cached.at > CACHE_TTL_MS) {
      cache.set(key, { promise: fetchSeries(mode, id), at: Date.now() });
    }
    setSeries(undefined);
    cache.get(key).promise.then((value) => {
      // Errores (totales o parciales) no se persisten: el próximo mount reintenta.
      if (value === null || value.some((w) => w.count === null)) cache.delete(key);
      if (alive) setSeries(value);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, reloadKey]);

  return { series, loading: series === undefined };
}

export default useDeliveredWeekly;
